import React, { FormEvent, useState } from "react";

import { TeamRequestForm, submitTeamRequest } from "../api/teamRequestApi";
import { logoUrl } from "../config/apps";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface RegisterProps {
  /** Pre-fills the team and is sent as a hidden team_id; set when the team is already known. */
  team?: { id: string; name: string };
  /** Drop the page chrome (logo, heading) when shown inside another page. */
  embedded?: boolean;
  onSubmitted?: () => void;
}

/** Rendered outside the sign-in gate: the person asking for access has no account yet. */
export const Register: React.FC<RegisterProps> = ({ team, embedded, onSubmitted }) => {
  const empty: TeamRequestForm = {
    firstName: "",
    lastName: "",
    email: "",
    team: team?.name ?? "",
    ...(team ? { team_id: team.id } : {}),
  };
  const [form, setForm] = useState<TeamRequestForm>(empty);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [success, setSuccess] = useState<string>();

  const set = (field: keyof TeamRequestForm) => (event: React.ChangeEvent<HTMLInputElement>) =>
    setForm((current) => ({ ...current, [field]: event.target.value }));

  const valid =
    EMAIL_PATTERN.test(form.email.trim()) &&
    Boolean(form.firstName.trim() && form.lastName.trim() && form.team.trim());

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!valid || busy) return;
    setBusy(true);
    setError(undefined);
    try {
      setSuccess(await submitTeamRequest(form));
      setForm(empty);
      onSubmitted?.();
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Could not process team request");
    } finally {
      setBusy(false);
    }
  };

  const Wrapper = embedded ? React.Fragment : "main";
  const wrapperProps = embedded ? {} : { className: "ps-page", style: { maxWidth: 520, margin: "0 auto" } };

  return (
    <Wrapper {...wrapperProps}>
      {!embedded && (
        <header className="ps-page-header">
          <div>
            <img src={logoUrl()} alt="BITE Test" height={32} />
            <h2>Request access</h2>
            <p>Tell us who you are and which team you need to join. A team admin will review your request.</p>
          </div>
        </header>
      )}

      <section className="ps-card">
        {success && (
          <p className="ps-alert ps-alert-success" role="status">
            {success}
          </p>
        )}
        {error && (
          <p className="ps-alert ps-alert-error" role="alert">
            {error}
          </p>
        )}
        <form onSubmit={submit}>
          {team && <input type="hidden" name="team_id" value={team.id} />}
          <label className="ps-field">
            <span>First name</span>
            <input type="text" value={form.firstName} disabled={busy} onChange={set("firstName")} autoComplete="given-name" />
          </label>
          <label className="ps-field">
            <span>Last name</span>
            <input type="text" value={form.lastName} disabled={busy} onChange={set("lastName")} autoComplete="family-name" />
          </label>
          <label className="ps-field">
            <span>Email</span>
            <input type="email" value={form.email} disabled={busy} onChange={set("email")} autoComplete="email" />
          </label>
          {!team && (
            <label className="ps-field">
              <span>Team</span>
              <input type="text" value={form.team} disabled={busy} onChange={set("team")} />
            </label>
          )}
          <div className="admin-actions">
            <button type="submit" className="ps-btn ps-btn-primary" disabled={busy || !valid}>
              {busy ? "Requesting…" : "Request"}
            </button>
          </div>
        </form>
      </section>
    </Wrapper>
  );
};
